/**
 * DeviceContext - App-wide device information provider
 * 
 * Wraps the app to provide device detection info to any component
 * without prop drilling.
 * 
 * Usage:
 *   // In App.js or index.js:
 *   <DeviceProvider>
 *     <App />
 *   </DeviceProvider>
 * 
 *   // In any component:
 *   const { isIOS, isMobile, screenSize } = useDevice();
 */

import React, { createContext, useContext } from 'react';
import { useDeviceDetect } from '@healthdesk/shared-hooks';

// Create the context
export const DeviceContext = createContext(undefined);

/**
 * DeviceProvider - Wraps children with device context
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 */
export function DeviceProvider({ children }) {
  const deviceInfo = useDeviceDetect();
  
  return (
    <DeviceContext.Provider value={deviceInfo}>
      {children}
    </DeviceContext.Provider>
  );
}

/**
 * useDevice - Hook to access device context
 * 
 * @returns {Object} Device information from context
 * @throws {Error} If used outside of DeviceProvider
 */
export function useDevice() {
  const context = useContext(DeviceContext);
  
  if (context === undefined) {
    throw new Error('useDevice must be used within a DeviceProvider');
  }
  
  return context;
}

export default DeviceContext;

